import React, { useState } from "react";
import api from "../api/axios"; // Standardized API instance with Interceptors
import Swal from 'sweetalert2';

// Synchronized Maritime Palette
const colors = {
  brand: "#1E293B",      // Deep Slate
  maritime: "#6D9BC3",   // Maritime Blue
  bg: "#F8FAFC", 
  text: "#0F172A",
  muted: "#64748B",
  border: "#E2E8F0",
  success: "#10B981"
};

function ProductCard({ product, highlight = false, showPickButton = false, onSuccess }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL || "https://maritime-supply-suite-backend.onrender.com";

  // Extract active tenant identifier from storage or fallback
  const tenantId = localStorage.getItem("adminId") || "global";

  /**
   * FIXED: Image path reconciler to prevent broken assets
   */
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://via.placeholder.com/300?text=ASSET+UNAVAILABLE";
    if (imagePath.startsWith("http")) return imagePath;
    
    const cleanPath = imagePath.replace(/^\/+/, "").replace(/^uploads\//, "");
    return `${API_BASE}/uploads/${cleanPath}`;
  };

  /**
   * FIXED: Buy Now Logic
   * Endpoint updated to /api/orders/buy via the interceptor-configured api instance with tenant headers
   */
  const handlePurchase = async () => {
    try {
      const result = await Swal.fire({
        title: 'Confirm Acquisition',
        text: `Do you wish to process the order for ${displayName}?`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonColor: colors.maritime,
        cancelButtonColor: colors.muted,
        confirmButtonText: 'Initialize Acquisition'
      });

      if (result.isConfirmed) {
        setIsProcessing(true);
        
        // POST to the buy endpoint with tenant header scoping
        const response = await api.post("/api/orders/buy", {
          productId: product._id,
          tenantId: tenantId !== "global" ? tenantId : undefined
        }, {
          headers: tenantId !== "global" ? { "x-tenant-id": tenantId } : {}
        });

        if (response.status === 200 || response.status === 201) {
          await Swal.fire({
            title: 'Success',
            text: 'Asset successfully added to your inventory.',
            icon: 'success',
            confirmButtonColor: colors.maritime
          });
          if (onSuccess) onSuccess();
        }
      }
    } catch (err) {
      console.error("[System] Purchase error:", err.response?.data?.message || err.message);
      Swal.fire('Clearance Failed', err.response?.data?.message || 'Unable to synchronize transaction.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * FIXED: Manifest Approval logic (Move from Inventory to Orders)
   * Corrected 404 by prepending /api and passing tenant headers
   */
  const handleAcceptShipment = async () => {
    // Determine the correct ID to target (the order itself)
    const targetId = product.orderId || product._id;
    if (!targetId) return;

    try {
      setIsProcessing(true);
      
      // Update status with tenant header isolation
      const res = await api.put(`/api/orders/${targetId}/status`, { 
        status: "pending" 
      }, {
        headers: tenantId !== "global" ? { "x-tenant-id": tenantId } : {}
      });

      if (res.status === 200 || res.status === 201) {
        setTimeout(() => {
          if (onSuccess) onSuccess(); 
          setIsProcessing(false);
        }, 800);
      } else {
        setIsProcessing(false);
      }
    } catch (err) {
      setIsProcessing(false);
      console.error("[System] Dispatch error:", err.response?.data?.message || err.message);
      alert("Clearance Failed: Unable to synchronize manifest status.");
    }
  };

  const renderUnitQuality = (rating = 5) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '8px 0' }}>
      <div style={{ display: 'flex', color: colors.maritime, fontSize: '10px' }}>
        {"★".repeat(Math.floor(rating))}
      </div>
      <span style={{ fontSize: '10px', color: colors.muted, fontWeight: '700', letterSpacing: '0.5px' }}>
        GRADE {rating.toFixed(1)} / SECURE
      </span>
    </div>
  );

  /**
   * DATA MAPPING
   */
  const displayName = product.name || (product.productId && product.productId.name) || "LOGISTICS UNIT";
  const displayPrice = product.baseValue || 
                        product.price || 
                        (product.productId && (product.productId.baseValue || product.productId.price)) || 
                        0;
  const displayMargin = product.profit !== undefined ? product.profit : 
                        (product.productId && (product.productId.profit || product.productId.commissionYield)) || 0;
  const displayImage = product.image || (product.productId && product.productId.image);

  return (
    <div data-tenant-scope={tenantId} style={{
        border: `1px solid ${highlight ? colors.maritime : colors.border}`,
        background: "#ffffff",
        padding: "16px",
        borderRadius: "8px",
        width: "100%",
        position: "relative",
        boxShadow: highlight ? "0 10px 20px rgba(30, 41, 59, 0.05)" : "none",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease"
      }}
    >
      {/* SECURITY OVERLAY */}
      {isProcessing && (
        <div style={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(30, 41, 59, 0.96)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "7px",
            zIndex: 10,
            textAlign: "center",
            backdropFilter: "blur(2px)"
          }}
        >
          <div className="uplink-spinner"></div>
          <span style={{ fontSize: "10px", fontWeight: "900", letterSpacing: "2px", marginTop: "15px" }}>
            UPLINKING...
          </span>
        </div>
      )}

      {/* ASSET STATUS TAG */}
      <div style={{
          position: "absolute",
          top: 10,
          left: 10,
          background: highlight ? colors.maritime : colors.brand,
          color: "#fff",
          fontSize: "8px",
          padding: "3px 7px",
          borderRadius: "3px",
          fontWeight: "900",
          zIndex: 2,
          letterSpacing: "1px"
        }}
      >
        {highlight ? "PRIORITY" : "STANDARD"}
      </div>

      <div>
        <div style={{ height: 160, overflow: "hidden", borderRadius: "4px", marginBottom: "12px", background: "#f1f5f9", border: `1px solid ${colors.border}` }}>
          <img
            src={getImageUrl(displayImage)}
            alt={displayName}
            style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isProcessing ? 0.3 : 1 }}
            onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=ASSET+UNAVAILABLE"; }}
          />
        </div>

        <div style={{ fontSize: '9px', color: colors.maritime, fontWeight: '800', marginBottom: '4px' }}>
          ASSET ID: {product._id ? product._id.slice(-10).toUpperCase() : "PENDING"}
        </div>
        
        <h3 style={{ margin: "0", fontSize: "14px", color: colors.brand, fontWeight: "800", lineHeight: "1.2", textTransform: 'uppercase' }}>
          {displayName}
        </h3>

        {renderUnitQuality(product.rating || 5)}

        <div style={{ background: colors.bg, padding: "10px", borderRadius: "4px", marginTop: "8px", border: `1px dotted ${colors.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={{ fontSize: "11px", color: colors.muted, fontWeight: "700" }}>BASE VALUATION:</span>
            <span style={{ fontSize: "11px", fontWeight: "800", color: colors.brand }}>
              ${Number(displayPrice).toLocaleString()}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: "11px", color: colors.muted, fontWeight: "700" }}>YIELD:</span>
            <span style={{ fontSize: "11px", fontWeight: "800", color: colors.success }}>
              +${Number(displayMargin).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* ACTION ROW */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        {!showPickButton && (
          <button
            onClick={handlePurchase}
            disabled={isProcessing}
            style={{
              flex: 1,
              padding: "10px 0",
              background: colors.maritime,
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: isProcessing ? "default" : "pointer",
              fontWeight: "900",
              fontSize: "11px",
              letterSpacing: "1px"
            }}
          >
            {isProcessing ? "PROCESSING..." : "BUY NOW"}
          </button>
        )}

        {/* SIDE-BY-SIDE INSPECT BUTTON */}
        <button
          onClick={() => {
            // Trigger your inspect modal, navigate, or run custom inspect handler
            if (product.onInspect) product.onInspect(product);
          }}
          style={{
            flex: 1,
            padding: "10px 0",
            background: colors.brand,
            color: "#fff",
            border: `1px solid ${colors.border}`,
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "900",
            fontSize: "11px",
            letterSpacing: "1px"
          }}
        >
          INSPECT
        </button>

        {showPickButton && (
          <button
            onClick={handleAcceptShipment}
            disabled={isProcessing}
            style={{
              flex: 1,
              padding: "10px 0",
              background: colors.brand,
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: isProcessing ? "default" : "pointer",
              fontWeight: "900",
              fontSize: "11px",
              letterSpacing: "1px"
            }}
          >
            {isProcessing ? "SYNCING..." : "Accept New Order"}
          </button>
        )}
      </div>

      <style>{`
        .uplink-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid rgba(255,255,255,0.1);
          border-top: 2px solid ${colors.maritime};
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default ProductCard;